import { Switch, Route, useLocation } from "wouter";
import { lazy, Suspense, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { CMSProvider } from "@/contexts/CMSContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { NeraChatbot } from "@/components/chat";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";

// Lazy load heavy pages to reduce initial bundle size
const Admin = lazy(() => import("@/pages/Admin"));
const AdminReports = lazy(() => import("@/pages/AdminReports"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
const FollowUpPage = lazy(() => import("@/pages/FollowUpPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPassword"));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePassword"));

/** Only render Nera for authenticated users (TPAs / employees) */
function AuthenticatedNera() {
  const { user } = useAuth();
  if (!user) return null;
  return <NeraChatbot />;
}

/**
 * Bounces any signed-in user whose profile.must_change_password is true
 * to the /change-password screen until they set their own password.
 * Skips /login and /change-password so the redirect chain can't loop.
 */
function PasswordChangeGuard({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (
      profile?.must_change_password &&
      location !== "/change-password" &&
      location !== "/login"
    ) {
      setLocation("/change-password");
    }
  }, [profile, location, setLocation]);

  return <>{children}</>;
}

function App() {
  return (
    <SidebarProvider defaultOpen={false}>
      <AuthProvider>
        <ChatProvider>
          <CMSProvider>
            <PasswordChangeGuard>
            <Switch>
              <Route path="/" component={Home} />
              <Route path="/login" component={Login} />
              <Route path="/reset-password">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <ResetPasswordPage />
                </Suspense>
              </Route>
              <Route path="/change-password">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <ChangePasswordPage />
                </Suspense>
              </Route>
              <Route path="/admin">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <ProtectedRoute
                    component={Admin}
                    allowedRoles={['internal_admin']}
                    fallbackPath="/admin/reports"
                  />
                </Suspense>
              </Route>
              <Route path="/admin/reports">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <ProtectedRoute component={AdminReports} />
                </Suspense>
              </Route>
              <Route path="/feedback/:campaignSlug">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <FeedbackPage />
                </Suspense>
              </Route>
              <Route path="/follow-up">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <FollowUpPage />
                </Suspense>
              </Route>
              <Route>404 Page Not Found</Route>
            </Switch>
            </PasswordChangeGuard>
            <Toaster />
            <AuthenticatedNera />
            <PWAUpdatePrompt />
          </CMSProvider>
        </ChatProvider>
      </AuthProvider>
    </SidebarProvider>
  );
}

export default App;
